import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { developerAccessTokenState } from "../global-state";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LoadingSpinner from "../common/loading";
import { SAND_CAT } from "../config";
import { useToast } from "../common/toast";
import { useTheme } from "@mui/material/styles";

const SchemaRagView = () => {
  const [developerAccessToken] = useRecoilState(developerAccessTokenState);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredFiles, setFilteredFiles] = useState([]);
  const { successToast, warningToast, errorToast } = useToast();
  const theme = useTheme();

  const handleSearch = async () => {
    if (!searchText.trim()) {
      setFilteredFiles(files);
      return;
    }

    setLoading(true);
    const searchUrl = `${SAND_CAT}/schema/rag/search?query=${searchText}`;
    try {
      const response = await fetch(searchUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          DeveloperAuthorization: `Bearer ${developerAccessToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data);
      }
      if (!data.error) {
        setFilteredFiles(data || []);
      }
    } catch (error) {
      errorToast("Error RAG retrieve schemas! Please retry.");
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const renderSchemaContent = file => {
    const jsonContent = JSON.parse(file.jsonContent);
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">
            Environment:{" "}
            <span style={{ color: theme.palette.primary.main }}>
              {file.env}
            </span>
          </Typography>
          <Typography variant="subtitle2" color="textSecondary">
            Relevance Score:{" "}
            <span style={{ color: theme.palette.primary.main }}>
              {(file.score * 100).toFixed(2)}%
            </span>
          </Typography>
        </Box>

        <Typography variant="subtitle2" color="textSecondary">
          Request Fields:
        </Typography>
        {jsonContent.requestFields.map((field, idx) => (
          <Box key={idx} sx={{ ml: 2, mb: 1 }}>
            <Typography variant="body2">
              <strong>{field.name}</strong> ({field.type})
              {field.optional ? " (Optional)" : " (Required)"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {field.description}
            </Typography>
          </Box>
        ))}

        <Typography variant="subtitle2" color="textSecondary">
          Response Fields:
        </Typography>
        {jsonContent.responseFields.length > 0 ? (
          jsonContent.responseFields.map((field, idx) => (
            <Box key={idx} sx={{ ml: 2, mb: 1 }}>
              <Typography variant="body2">
                <strong>{field.name}</strong> ({field.type})
                {field.optional ? " (Optional)" : " (Required)"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {field.description}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ ml: 2 }}>
            No response fields
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ px: 2, py: 1 }}>
        <Paper
          elevation={0}
          component="form"
          sx={{
            p: "2px 4px",
            display: "flex",
            alignItems: "center",
            width: "100%",
            mb: 1,
          }}
          onSubmit={e => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Search schemas..."
            name="search"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyPress={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <IconButton sx={{ p: "10px" }} onClick={handleSearch}>
            <SearchIcon />
          </IconButton>
        </Paper>
      </Box>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <Box sx={{ px: 2 }}>
          {filteredFiles.map((file, index) => (
            <Accordion key={index}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
              >
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" component="div">
                      {file.namespace}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {file.description}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>{renderSchemaContent(file)}</AccordionDetails>
            </Accordion>
          ))}
          {filteredFiles.length === 0 && (
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography>No schemas found</Typography>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SchemaRagView;
